import React from "react";

function StatusIcon({ tone }) {
  if (tone === "error") {
    return (
      <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9.303 3.376c.866 1.5-.217 3.374-1.948 3.374H4.645c-1.73 0-2.813-1.874-1.948-3.374l7.355-12.75c.865-1.5 3.03-1.5 3.896 0l7.355 12.75ZM12 16.5h.008v.008H12V16.5Z" />
      </svg>
    );
  }

  return (
    <svg className="h-6 w-6 animate-pulse" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17 17.25 21A2.652 2.652 0 0 0 21 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 1 1-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 0 0 4.486-6.336l-3.276 3.277a3.004 3.004 0 0 1-2.25-2.25l3.276-3.276a4.5 4.5 0 0 0-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75 3.75 2.25 7.5 4.5v1.409l4.26 4.26m-1.745 1.437 1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008Z" />
    </svg>
  );
}

export default function GameLoadOverlay({
  hasError,
  accessError,
  loadingMessage,
  gameLoadStatus,
  progress,
}) {
  const progressPercent = Math.min(100, Math.max(0, Math.round(progress * 100)));

  return (
    <div className="pointer-events-auto absolute inset-0 z-10 flex items-center justify-center bg-[radial-gradient(circle_at_50%_30%,rgba(79,70,229,0.22),transparent_55%),linear-gradient(145deg,#080d1e,#020617)] p-6">
      {hasError ? (
        <div role="alert" className="max-w-lg rounded-3xl border border-rose-400/20 bg-rose-500/10 p-6 text-center shadow-2xl backdrop-blur sm:p-8">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-400/15 text-rose-300"><StatusIcon tone="error" /></div>
          <h2 className="mt-4 text-xl font-extrabold text-white">Game couldn't start</h2>
          <p className="mt-2 text-sm leading-6 text-rose-100/75">{accessError || "Something interrupted the game while it was loading."}</p>
          <button type="button" onClick={() => window.location.reload()} className="mt-5 rounded-xl bg-white px-5 py-2.5 text-sm font-bold text-slate-950 transition hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300">Try again</button>
        </div>
      ) : (
        <div className="w-full max-w-sm text-center" role="status" aria-live="polite">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-indigo-300/20 bg-indigo-400/10 text-indigo-200 shadow-lg shadow-indigo-950/50"><StatusIcon /></div>
          <h2 className="mt-5 text-lg font-extrabold text-white">{loadingMessage}</h2>
          <p className="mt-1 text-sm text-indigo-100/55">The game is downloading. You can use the tutorial while you wait.</p>
          <div
            className="mt-6 h-2 overflow-hidden rounded-full bg-white/10"
            role={gameLoadStatus === "loading" ? "progressbar" : undefined}
            aria-label={gameLoadStatus === "loading" ? "Game download progress" : undefined}
            aria-valuemin={gameLoadStatus === "loading" ? 0 : undefined}
            aria-valuemax={gameLoadStatus === "loading" ? 100 : undefined}
            aria-valuenow={gameLoadStatus === "loading" ? progressPercent : undefined}
          >
            <div
              className={`h-full rounded-full bg-gradient-to-r from-indigo-500 via-violet-400 to-pink-400 transition-[width] duration-300 ${gameLoadStatus === "loading" ? "" : "animate-pulse"}`}
              style={{ width: `${gameLoadStatus === "loading" ? Math.max(5, progressPercent) : 12}%` }}
            />
          </div>
          <div className="mt-2 text-right text-xs font-bold tabular-nums text-indigo-200/60">
            {gameLoadStatus === "loading" ? `${progressPercent}%` : "Loading"}
          </div>
        </div>
      )}
    </div>
  );
}
